import graphql__797758c0be98e34e8c91 from "./__generated__/ProjectDetailsViewRenameProjectMutation.graphql.ts";
import graphql__67432fdccd25992e7514 from "./__generated__/ProjectDetailsViewUpdateProjectPurchaseOrderReferenceMutation.graphql.ts";
import graphql__ec7e25cbf5dda2e23348 from "./__generated__/ProjectDetailsViewUpdateProjectCustomerOrderNotesMutation.graphql.ts";
import graphql__d001f9283af4d575826c from "./__generated__/ProjectDetailsViewUpdateProjectBillingMutation.graphql.ts";
import graphql__e3d0004fdbd90b9051fd from "./__generated__/ProjectDetailsView_project.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useOutletContext} from "react-router";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import {useFragment, useRelayEnvironment} from "react-relay/hooks";
import Panel from "./presentational/Panel";
import EditableDataListItem from "./form/EditableDataListItem";
import EditableDataListField, {FieldDataType} from "./form/EditableDataListField";
import ActionButton from "./presentational/button/ActionButton";
import DeleteProjectDialog from "./dialog/DeleteProjectDialog";
import DataListItem from "./presentational/DataListItem";
import {languages} from "../Languages";
import Dinero from "dinero.js";
import Label from "./form/Label";
import Section from "./form/Section";
import Row from "./form/Row";
import {faBoxArchive, faTrash, faWavePulse} from "@fortawesome/pro-regular-svg-icons";
import ArchiveProjectDialog from "./dialog/ArchiveProjectDialog";
import ReactivateProjectDialog from "./dialog/ReactivateProjectDialog";
import {useErrorContext} from "../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useIntl} from "react-intl";

type Props = {}

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type ProjectDetailsViewProps = Props & ConnectedProps<typeof connector>;

const handleRenameProjectSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__797758c0be98e34e8c91,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToRenameProject"
        }
    )
}

const handleUpdateProjectPurchaseOrderReferenceSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__67432fdccd25992e7514,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdatePurchaseOrderReference"
        }
    )
}

const handleUpdateProjectCustomerOrderNotesSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__ec7e25cbf5dda2e23348,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdateCustomerOrderNotes"
        }
    )
}

const handleUpdateProjectBillingSubmission = (input, setEditingActive, relayEnvironment, addError) => {
    input.hourlyRate = input.hourlyRate === null ? null : Math.floor(input.hourlyRate)
    input.fixedFee = input.fixedFee === null ? null : Math.floor(input.fixedFee)

    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__d001f9283af4d575826c,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: "errors.failedToUpdateProjectBilling"
        }
    )
}

const ProjectDetailsView: React.FC<ProjectDetailsViewProps> = (props) => {
    const intl = useIntl();
    const queryRef: any = useOutletContext();
    const {addError} = useErrorContext();
    const project = useFragment(
        graphql__e3d0004fdbd90b9051fd,
        queryRef
    );
    const relayEnvironment = useRelayEnvironment();
    const {dispatchShowModal, dispatchHideModal} = props;

    const showDeleteProjectDialog = () => {
        dispatchShowModal({
            body: (<DeleteProjectDialog queryRef={project} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const showArchiveProjectDialog = () => {
        dispatchShowModal({
            body: (<ArchiveProjectDialog queryRef={project} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const showReactivateProjectDialog = () => {
        dispatchShowModal({
            body: (<ReactivateProjectDialog queryRef={project} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const formattedHourlyRate = Dinero({amount: project.hourlyRate ?? 0}).toFormat('$0.00')
    const formattedFixedFee = Dinero({amount: project.fixedFee ?? 0}).toFormat('$0.00')

    return (
        <>
            <Panel>
                <dl className="divide-y divide-gray-200">
                    <EditableDataListItem
                        label={intl.formatMessage({id: 'project.field.projectName'})}
                        fields={(<EditableDataListField fieldDataType={FieldDataType.text} name="projectName" autoFocus={true}/>)}
                        initialValues={{id: project.id, projectName: project.projectName}}
                        handleSubmission={(values, setEditingActive) => handleRenameProjectSubmission(values, setEditingActive, relayEnvironment, addError)}
                    >
                        {project.projectName}
                    </EditableDataListItem>
                    <DataListItem label={intl.formatMessage({id: 'project.field.documentLanguage'})}>{languages[project.language]}</DataListItem>
                </dl>
            </Panel>
            <Panel>
                <dl className="divide-y divide-gray-200">
                    <EditableDataListItem
                        label={intl.formatMessage({id: 'project.field.billing'})}
                        fields={
                            (
                            <Section>
                                <Row>
                                    <Label>{intl.formatMessage({id: 'project.field.billingMode'})}</Label>
                                    <EditableDataListField fieldDataType={FieldDataType.select} name="billingMode">
                                        <option value="NON_BILLABLE">{intl.formatMessage({id: 'project.billingMode.nonBillable'})}</option>
                                        <option value="TIME_AND_MATERIALS">{intl.formatMessage({id: 'project.billingMode.timeAndMaterials'})}</option>
                                        <option value="FIXED_FEE">{intl.formatMessage({id: 'project.billingMode.fixedFee'})}</option>
                                    </EditableDataListField>
                                </Row>
                                <Row>
                                    <Label>{intl.formatMessage({id: 'project.field.hourlyRate'})}</Label>
                                    <EditableDataListField name="hourlyRate" fieldDataType={FieldDataType.text}/>
                                </Row>
                                <Row>
                                    <Label>{intl.formatMessage({id: 'project.field.fixedFee'})}</Label>
                                    <EditableDataListField name="fixedFee" fieldDataType={FieldDataType.text}/>
                                </Row>
                            </Section>
                        )}
                        initialValues={{
                            id: project.id,
                            billingMode: project.billingMode,
                            hourlyRate: project.hourlyRate === undefined ? 0 : project.hourlyRate,
                            fixedFee: project.fixedFee === undefined ? 0 : project.fixedFee,
                        }}
                        handleSubmission={(values, setEditingActive) => handleUpdateProjectBillingSubmission(values, setEditingActive, relayEnvironment, addError)}
                    >
                        {project.billingMode === 'NON_BILLABLE' && (<>{intl.formatMessage({id: 'project.billingMode.nonBillable'})}</>)}
                        {project.billingMode === 'TIME_AND_MATERIALS' && (<>{intl.formatMessage({id: 'project.billing.timeAndMaterialsCharge'}, {hourlyRate: formattedHourlyRate})}</>)}
                        {project.billingMode === 'FIXED_FEE' && (<>{intl.formatMessage({id: 'project.billing.fixedFeeCharge'}, {fixedFee: formattedFixedFee})}</>)}
                    </EditableDataListItem>
                </dl>
            </Panel>
            <Panel>
                <dl className="divide-y divide-gray-200">
                    <EditableDataListItem
                        label={intl.formatMessage({id: 'project.field.purchaseOrderReference'})}
                        fields={(<EditableDataListField fieldDataType={FieldDataType.text} name="purchaseOrderReference" autoFocus={true}/>)}
                        initialValues={{id: project.id, purchaseOrderReference: project.purchaseOrderReference ?? ''}}
                        handleSubmission={(values, setEditingActive) => handleUpdateProjectPurchaseOrderReferenceSubmission(values, setEditingActive, relayEnvironment, addError)}
                    >
                        {project.purchaseOrderReference}
                    </EditableDataListItem>
                    <EditableDataListItem
                        label={intl.formatMessage({id: 'project.field.customerOrderNotes'})}
                        fields={(<EditableDataListField fieldDataType={FieldDataType.text} name="customerOrderNotes" autoFocus={true}/>)}
                        initialValues={{id: project.id, customerOrderNotes: project.customerOrderNotes ?? ''}}
                        handleSubmission={(values, setEditingActive) => handleUpdateProjectCustomerOrderNotesSubmission(values, setEditingActive, relayEnvironment, addError)}
                    >
                        {project.customerOrderNotes}
                    </EditableDataListItem>
                </dl>
            </Panel>
            <div className="flex space-x-4 justify-end">
                {project.state === 'ACTIVE' && (<ActionButton onClick={showDeleteProjectDialog} icon={faTrash} label={intl.formatMessage({id: 'button.deleteProject'})}/>)}
                {project.state === 'ACTIVE' && (<ActionButton onClick={showArchiveProjectDialog} icon={faBoxArchive} label={intl.formatMessage({id: 'button.archiveProject'})}/>)}
                {project.state === 'ARCHIVED' && (<ActionButton onClick={showReactivateProjectDialog} icon={faWavePulse} label={intl.formatMessage({id: 'button.reactivateProject'})}/>)}
            </div>
        </>
    )
}

export default connector(ProjectDetailsView);
